/**
 * [TODO-928] - We should extract these components out and making them the standard way in any slide editor to render slides/groups
 */
import React, { useCallback, useMemo } from 'react';
import { ScrollView, StyleSheet, ViewStyle } from 'react-native';
import { DocumentAccessLevel, FileType, Page } from '@alucio/aws-beacon-amplify/src/models';
import { DNABox, DNAText, DNAIcon, Iffy, Stack, util } from '@alucio/lux-ui';
import colors from '@alucio/lux-ui/src/theming/themes/alucio/colors';
import {
  CommonIcon,
  DNAChip,
  DNAFileTypeChip,
  DNAThumbnail,
  DNAThumbnailProps,
  DNAPopover,
  ThumbnailFooterButton,
  SlidePreviewModal,
  DNAModalActions,
} from 'src/components/DNA';
import {
  GroupStatus,
  ModifiedPayloadGroup,
  unavailableStatus,
  usePresentationBuilderState,
} from 'src/components/PresentationBuilder/state/PresentationBuilderStateProvider';
import { GroupItemOverlay } from './Overlays';
import { detectArchivedFileKeyPath } from 'src/components/SlideSelector/useThumbnailSelector';
import { ThumbnailSize, thumbnailSizeDimensions } from 'src/hooks/useThumbnailSize/useThumbnailSize';
import { useAppSettings } from 'src/state/context/AppSettings';
import { ThumbnailPage } from 'src/components/SlideSelector/SlideSelector';
import { ContentPageData } from 'src/hooks/useContentPageData/useContentPageData';
import { DocumentVersionORM } from 'src/types/orms';
import {
  usePresentationSelector,
} from 'src/components/PresentationBuilder/PresentationSelector/PresentationSelectorStateProvider';
import { findPage } from 'src/state/machines/findReplacement/findReplacementUtils';
import { MagnifyConfig, MagnifyPresets } from 'src/components/DNA/Thumbnail/DNAThumbnail';
import { useDispatch } from 'react-redux';

export const S = StyleSheet.create({
  outlineBorder: {
    borderRadius: 4,
    borderWidth: 3,
    padding: 4,
  },
  thumbnailContainer: {
    paddingTop: 4,
    paddingHorizontal: 4,
  },
  groupedContainer: {
    paddingTop: 64,
    paddingHorizontal: 16,
    paddingBottom: 16,
  },
  // Offset the container so groups aligned with non-grouped items, overlay is also offset though
  groupedOffset: {
    marginHorizontal: -16,
  },
  thumbnailBackdrop: {
    backgroundColor: colors['color-gray-100'],
    borderRadius: 4,
  },
  thumbnailBorder: {
    borderColor: colors['color-gray-100'],
    borderRadius: 6,
    borderWidth: 3,
  },
  disableThumbnailBackdrop: {
    backgroundColor: colors['color-warning-5'],
    borderColor: colors['color-text-basic'],
    borderRadius: 4,
    borderWidth: 3,
  },
  disableThumbnailBorder: {
    borderColor: colors['color-warning-500'],
    borderRadius: 6,
    borderWidth: 3,
  },
  selectedThumbnailBorder: {
    borderColor: colors['color-brand2-500'],
    borderRadius: 6,
    borderWidth: 3,
  },
  footerOffset: {
    paddingHorizontal: 6,
  },
  updatedIcon: {
    marginRight: 6,
  },
  updatedText: {
    fontWeight: '700',
  },
})

type UseMagnifyConfigParams = {
  page: Page;
  preview: any;
  isFindAndReplace: boolean;
  isUnavailable: boolean;
  isLocked?: boolean;
  isSingleSlide: boolean;
};

/**
 * Custom hook to manage magnify configuration for slide thumbnails
 */
const useMagnifyConfig = ({
  page,
  preview,
  isFindAndReplace,
  isUnavailable,
  isLocked,
  isSingleSlide,
}: UseMagnifyConfigParams): MagnifyConfig => {
  const dispatch = useDispatch();

  const onMagnify = useCallback(() => {
    dispatch(DNAModalActions.setModal({
      isVisible: true,
      allowBackdropCancel: true,
      component: (modalProps) => (
        <SlidePreviewModal
          pageNumber={page.number}
          payload={preview}
          disableNavigation={true}
          {...modalProps}
        />
      ),
    }));
  }, [dispatch, page.number, preview]);

  const magnifyConfigThreeDots: MagnifyConfig = useMemo(() => ({
    enabled: true,
    disableNavigation: true,
    buttonStyle: { marginTop: 7, marginRight: 8 },
    onMagnify: () => onMagnify(),
  }), [onMagnify]);

  const magnifyConfig: MagnifyConfig = useMemo(() => {
    if (isFindAndReplace || isUnavailable) {
      return MagnifyPresets[DNAThumbnail.MagnifyPresets.NONE];
    }

    if (isLocked) {
      return MagnifyPresets[DNAThumbnail.MagnifyPresets.SINGLE];
    }

    return isSingleSlide
      ? magnifyConfigThreeDots
      : {
        ...MagnifyPresets[DNAThumbnail.MagnifyPresets.SINGLE],
        buttonStyle: magnifyConfigThreeDots.buttonStyle,
      };
  }, [isFindAndReplace, isSingleSlide, isLocked, isUnavailable, magnifyConfigThreeDots]);

  return magnifyConfig;
};

type SlideProps = {
  group: ModifiedPayloadGroup,
  page: Page,
  displayIdx: number,
  enableOverlay?: boolean,
  style?: ViewStyle,
  parentRef?: React.RefObject<ScrollView>
} & Pick<DNAThumbnailProps, 'height' | 'width' | 'onCheck' | 'mode' | 'checked' | 'deferLoad'>

type DisplaySlideData = {
  hasMapping: boolean,
  documentVersionORM?: DocumentVersionORM,
  s3URL: string,
  title: string,
}

const getDisplaySlideData = (
  group: ModifiedPayloadGroup,
  page: Page,
  autoUpdateMapping: Record<string, string>,
  editorThumbnailSize: ThumbnailSize,
  allContentPageDataVersions: { [documentVersionId: string]: ContentPageData[] },
): DisplaySlideData => {
  const isSingleSlideGroup = group.pages.length === 1;
  if (isSingleSlideGroup) {
    const slideMapping: string | undefined = autoUpdateMapping[group.pages[0].pageId];
    const hasMapping = !!slideMapping;
    const documentVersionORM = hasMapping
      ? group.documentVersionORM?.relations.documentORM.relations.version.latestPublishedDocumentVersionORM
      : group.documentVersionORM;
    const documentVersionId = documentVersionORM?.model.id || '';
    const targetPage = hasMapping ? findPage(slideMapping, documentVersionORM) || page : page;
    const s3URL = detectArchivedFileKeyPath(documentVersionORM?.model, targetPage, editorThumbnailSize);
    const contentPageData = allContentPageDataVersions[documentVersionId] ?? [];
    const title = contentPageData.find(pageData => pageData.pageId === targetPage.pageId)?.title ||
      contentPageData.find(pageData => pageData.documentPageNumber === targetPage.number)?.title ||
      contentPageData[targetPage.number - 1]?.title || ''
    return {
      hasMapping,
      documentVersionORM,
      s3URL,
      title,
    }
  } else {
    const hasMapping = !!autoUpdateMapping[group.id];
    const documentVersionORM = hasMapping
      ? group.documentVersionORM?.relations.documentORM.relations.version.latestPublishedDocumentVersionORM
      : group.documentVersionORM;
    const currentDocVerId = group.documentVersionORM?.model.id;
    const documentVersionId = documentVersionORM?.model.id || '';
    const slideMapping = hasMapping && currentDocVerId && documentVersionId
      ? page.pageId.replace(currentDocVerId, documentVersionId) : undefined;
    const targetPage = hasMapping && slideMapping ? findPage(slideMapping, documentVersionORM) || page : page;
    const s3URL = detectArchivedFileKeyPath(documentVersionORM?.model, targetPage, editorThumbnailSize);
    const contentPageData = allContentPageDataVersions[documentVersionId] ?? [];
    const title = contentPageData.find(pageData => pageData.pageId === targetPage.pageId)?.title ||
      contentPageData.find(pageData => pageData.documentPageNumber === targetPage.number)?.title ||
      contentPageData[targetPage.number - 1]?.title || ''
    return {
      hasMapping,
      documentVersionORM,
      s3URL,
      title,
    }
  }
}

export const Slide: React.FC<SlideProps> = (
  { group, page, displayIdx, enableOverlay, style, parentRef, checked, onCheck, mode, deferLoad },
) => {
  const {
    editorThumbnailSize,
    displayPageSourceFile,
    isLocked,
    openFindReplacement,
    onRemoveGroup,
    onRemoveAllUnavailableGroups,
    autoUpdateMapping,
    allContentPageDataVersions,
  } = usePresentationBuilderState();
  const { isOnline } = useAppSettings()
  const { active } = usePresentationSelector()
  const {
    hasMapping,
    documentVersionORM,
    s3URL,
    title,
  } = getDisplaySlideData(group, page, autoUpdateMapping, editorThumbnailSize, allContentPageDataVersions);

  const visibleTypeBadges: DocumentVersionORM['model']['type'][] = [FileType.WEB, FileType.HTML, FileType.MP4];
  const isVisivleTypeBadge = group.documentVersionORM &&
    visibleTypeBadges.includes(group.documentVersionORM.model.type);
  const isFromPersonalDocument =
    group.documentVersionORM?.relations.documentORM.model.accessLevel === DocumentAccessLevel.USER;

  const thumbnailDimensions = {
    height: thumbnailSizeDimensions[editorThumbnailSize].height,
    width: thumbnailSizeDimensions[editorThumbnailSize].width,
  };

  // Prepare content page data
  const contentPageData = React.useMemo(() => {
    if (!documentVersionORM) return [];
    return allContentPageDataVersions[documentVersionORM.model.id] ?? [];
  }, [documentVersionORM, allContentPageDataVersions]);

  // Prepare preview data for magnify functionality
  const preview = React.useMemo(() => {
    if (!documentVersionORM) return undefined;

    return {
      pages: documentVersionORM.meta.allPages,
      docVerORM: documentVersionORM,
      getSlideTitle: (num: number) => contentPageData.find(
        pageData => pageData.documentPageNumber === num,
      )?.title ?? `Slide ${num}`,
    };
  }, [documentVersionORM, contentPageData]);

  const { isContentCached } = documentVersionORM?.meta?.assets ?? { }

  const finalStyle = StyleSheet.flatten([
    S.thumbnailContainer,
    style,
  ])

  const borderColor:ViewStyle = {
    borderColor: active?.data.current?.itemId === group.id
      ? colors['color-brand2-500']
      : colors['color-gray-100'],
  }

  const isSingleSlide = group.pages.length === 1;
  const isUnavailable = !!unavailableStatus[group.groupStatus];
  const isFindAndReplace = group.groupStatus === GroupStatus.MAJOR_UPDATE;
  const showRemoveSlideBtn = !isLocked && isUnavailable && isSingleSlide;
  const showReviewOrReplaceBtn = !showRemoveSlideBtn && hasMapping && isSingleSlide;
  const showFindReplacementBtn = !isLocked && isFindAndReplace && isSingleSlide && !hasMapping;
  const showUpdatedCheckMarkForGroupSlides = !isUnavailable && !isSingleSlide && hasMapping;

  const magnifyConfig = useMagnifyConfig({
    page,
    preview,
    isFindAndReplace,
    isUnavailable,
    isLocked,
    isSingleSlide,
  });
  // [Note]: BEAC-6694, disable text insert from custom deck editor for now.
  // const showInsertTextBtn = enableSlideTextInsertion &&
  //   !showRemoveSlideBtn &&
  //   !showFindReplacementBtn &&
  //   !showReviewOrReplaceBtn &&
  //   !!contentPageData[page.number - 1]?.enableSlideTextInsertion;

  const thumbnailStatusBorder = (isSingleSlide && isFindAndReplace) ? hasMapping ? 'success' : 'warning' : undefined

  return (
    <DNABox testID="slide-thumbnail" appearance="col" spacing="xs">
      {/* [TODO-928] - Consider making this composable instead of prop driven */}
      <GroupItemOverlay
        group={group}
        page={page}
        enabled={enableOverlay}
        isLocked={isLocked}
        magnifyConfig={magnifyConfig}
      >
        {/* Thumbnail Container */}
        <DNABox style={[finalStyle, borderColor]}>
          {
            !isUnavailable
              ? (
                <Stack anchor="center">
                  <Stack.Layer>
                    <DNAThumbnail.Lazy
                      overlayText={displayPageSourceFile ? group.documentVersionORM?.model.title : undefined}
                      style={S.thumbnailBackdrop}
                      s3URL={s3URL}
                      pageNumber={page.number}
                      parentRef={parentRef}
                      variant={DNAThumbnail.Variants.INFO}
                      magnifyConfig={magnifyConfig}
                      preview={preview}
                      mode={mode}
                      isHidden={false}
                      sourceIsHidden={false}
                      disabled={isSingleSlide && isFindAndReplace}
                      onCheck={onCheck}
                      checked={checked}
                      status={thumbnailStatusBorder}
                      deferLoad={deferLoad}
                      {...thumbnailDimensions}
                    />
                  </Stack.Layer>
                  {/* offline layout */}
                  <Stack.Layer>
                    {
                      (!isOnline && !isContentCached)
                        ? (<DNAIcon.Styled name="cloud-slash" size="xl" />)
                        : null
                    }
                  </Stack.Layer>
                </Stack>
              )
              //  - If a slide in a group is no longer "valid"
              //    show a fallback holder (with additional details/indicators)
              : (
                <DNABox
                  style={util.mergeStyles(
                    undefined,
                    [S.disableThumbnailBorder, isSingleSlide],
                    [S.thumbnailBorder, !isSingleSlide],
                    [S.selectedThumbnailBorder, checked],
                  )}
                >
                  <Stack anchor="center">
                    <Stack.Layer>
                      <DNABox style={[S.disableThumbnailBackdrop, thumbnailDimensions]} />
                    </Stack.Layer>
                    <Stack.Layer>
                      <Iffy is={isUnavailable}>
                        <DNABox fill appearance="col">
                          <DNAText bold b1 status="warning">
                            {group.groupStatus === GroupStatus.DELETED
                              ? 'Source file is unavailable'
                              : `Source file was ${group.groupStatus.toLowerCase()}`
                            }
                          </DNAText>
                        </DNABox>
                      </Iffy>
                    </Stack.Layer>
                  </Stack>
                </DNABox>
              )
          }
        </DNABox>
      </GroupItemOverlay>
      <DNABox
        fill
        appearance="col"
        testID="slide-footer"
        style={S.footerOffset}
        spacing="sm"
      >
        <SlideFooter
          isFromPersonalDocument={isFromPersonalDocument}
          page={page}
          displayIdx={displayIdx}
          documentVersionORM={isVisivleTypeBadge ? group.documentVersionORM : undefined}
          title={title}
          width={thumbnailDimensions.width + 10}
        />
        {/* UPDATED TEXT */}
        <Iffy is={showUpdatedCheckMarkForGroupSlides}>
          <DNABox alignX="center">
            <CommonIcon
              testID="updated_slide_icon"
              variant="check"
              style={S.updatedIcon}
            />
            <DNAText
              p1
              status="success"
              style={S.updatedText}
            >
              Updated
            </DNAText>
          </DNABox>
        </Iffy>
        {/* REMOVE SLIDE BUTTON */}
        <Iffy is={showRemoveSlideBtn}>
          <ThumbnailFooterButton
            variant="RemoveSlide"
            onPress={() => onRemoveGroup(group)}
            onPressSecondaryBtn={onRemoveAllUnavailableGroups}
          />
        </Iffy>
        {/* REVIEW OR REPLACE BUTTON */}
        <Iffy is={showReviewOrReplaceBtn}>
          <ThumbnailFooterButton
            variant="ReviewReplace"
            onPress={() => openFindReplacement(group)}
          />
        </Iffy>
        {/* FIND REPLACEMENT BUTTON */}
        <Iffy is={showFindReplacementBtn}>
          <ThumbnailFooterButton
            variant="FindReplacement"
            onPress={() => openFindReplacement(group)}
          />
        </Iffy>
        {/* INSERT TEXT BUTTON [Note]: BEAC-6694, disable text insert from custom deck editor for now. */}
        {/* <Iffy is={showInsertTextBtn}>
          <ThumbnailFooterButton
            variant="InsertText"
            onPress={() => onPreview(displayIdx, 'textInsertion')}
          />
          </Iffy> */}
      </DNABox>
    </DNABox>
  )
}

interface SlideFooterProps {
  page: Page,
  isFromPersonalDocument?: boolean,
  displayIdx: number,
  documentVersionORM?: DocumentVersionORM,
  title?: string,
  width?: number,
}
export const SlideFooter: React.FC<SlideFooterProps> = ({
  isFromPersonalDocument,
  page:unAssertedPage,
  displayIdx,
  documentVersionORM,
  width = 220,
  title,

}) => {
  const { selectedGroups, associatedParentsMap } = usePresentationBuilderState();

  /** TODO: Remove this assertion in this ticket
   * https://alucioinc.atlassian.net/browse/BEAC-3670
   */
  const page = unAssertedPage as ThumbnailPage

  const currentAssociatedParents: number[] = []

  if (associatedParentsMap.get(page.pageId) && !page.isRequired) {
    const allSlideIds: string[] = []
    selectedGroups.forEach(group => {
      group.pages.forEach(page => {
        allSlideIds.push(page.pageId)
      })
    })
    page.parentIds?.forEach(id => {
      if (allSlideIds.indexOf(id) !== -1) {
        currentAssociatedParents.push(allSlideIds.indexOf(id) + 1)
      }
    })
  }
  const formattedCurrentAssociatedParents = currentAssociatedParents.sort().join(', ')
  const hasAssociatedParent = associatedParentsMap.get(page.pageId) && !page.isRequired
  const slideFooterText =
    ((displayIdx !== undefined && displayIdx !== -1) ? `${displayIdx}. ` : '') + title

  return (
    <DNABox fill spacing="between" style={{ width }} childFill={0}>
      <DNAText testID="slide-number" numberOfLines={1}>{slideFooterText}</DNAText>
      <Iffy is={page.isRequired}>
        <DNAChip.Beacon variant="REQUIRED" style={{ marginLeft: 10 }} />
      </Iffy>
      <Iffy is={isFromPersonalDocument}>
        <DNAChip.Beacon variant="DOCUMENT_MY_UPLOADS" style={{ marginLeft: 10 }} />
      </Iffy>
      <Iffy is={hasAssociatedParent}>
        <DNAPopover key={page.pageId} >
          <DNAPopover.Anchor>
            <DNAChip.Beacon variant="REQUIRED" iconLeft="link" style={{ marginLeft: 10 }} />
          </DNAPopover.Anchor>
          <DNAPopover.Content offset={2}>
            <DNAText
              style={{ color: colors['color-text-white'], marginHorizontal: 12, width: 300 }}
            >
              {`This slide is required because you added the following slides: ${formattedCurrentAssociatedParents}`}
            </DNAText>
          </DNAPopover.Content>
        </DNAPopover>
      </Iffy>
      {documentVersionORM && (
        <DNAFileTypeChip orm={documentVersionORM} style={{ marginLeft: 10 }} />
      )}
    </DNABox>
  )
}

export default Slide
